import type React from "react"
import { useState, useEffect, useRef } from "react"
import { useAuth } from "../context/AuthContext"
import api from "../utils/api"
import { io, type Socket } from "socket.io-client"
import Navbar from "../components/Navbar"
import "./Chat.css"
import { translate } from "../utils/translator"

interface ActiveConversation {
  id: number
}

interface Message {
  id: number
  conversation_id: number
  sender_id: number
  sender_username: string
  receiver_id: number
  receiver_username: string
  content: string
  is_read: boolean
  created_at: string
  original?: string 
}

interface Conversation {
  id: number
  user1: { id: number; username: string; profile_picture?: string }
  user2: { id: number; username: string; profile_picture?: string }
  last_message?: Message
  created_at: string
  updated_at: string
}

// Language detection utility
const detectLanguage = async (text: string): Promise<string> => {
  try {
    const response = await fetch("https://utm-emart-translation-system.onrender.com/detect", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q: text }),
    })
    
    if (response.ok) {
      const data = await response.json()
      return data[0]?.language || "en"
    }
  } catch (error) {
    console.error("🔍 Language detection failed:", error)
  }
  return "en" // fallback to English
}

const Chat: React.FC = () => {
  const { user } = useAuth()
  const token = localStorage.getItem("token")
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket | null>(null)
  const [isTyping, setIsTyping] = useState(false)
  const [otherUserTyping, setOtherUserTyping] = useState(false)
  const [unreadCounts, setUnreadCounts] = useState<{ [convId: number]: number }>({})
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(false)

  // Store detected user language per conversation
  const [userLanguages, setUserLanguages] = useState<{ [convId: number]: string }>({})

  const fetchConversations = async () => {
    try {
      setLoading(true)
      const response = await api.get("/chat/conversations")
      const convs: Conversation[] = response.data.conversations
      setConversations(convs)

      // Count unread messages per conversation
      const counts: { [convId: number]: number } = {}
      convs.forEach((conv) => {
        const msg = conv.last_message
        if (msg && msg.receiver_id === user?.id && !msg.is_read) {
          counts[conv.id] = 1 // Just flag it
        }
      })
      setUnreadCounts(counts)
    } catch (err) {
      setError("Failed to load conversations")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const adjustUtcString = (utcStr: string) => {
    const utcWithZ = utcStr.endsWith("Z") ? utcStr : `${utcStr}Z`
    const date = new Date(utcWithZ)
    return date.toLocaleTimeString("en-MY", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kuala_Lumpur",
    })
  }

  // FIXED: Handle translation toggle properly without race conditions
  const handleTranslationToggle = async () => {
    const newTranslationState = !isTranslationEnabled
    setIsTranslationEnabled(newTranslationState)
    
    // If turning translation ON and we have an active conversation
    if (newTranslationState && activeConversation && messages.length > 0) {
      const userLang = userLanguages[activeConversation.id] || "en"
      console.log(`🔄 Applying translations to existing messages with language: ${userLang}`)
      
      // Translate existing messages from other users
      const translatedMessages = await Promise.all(
        messages.map(async (msg: Message) => {
          if (msg.sender_id === user?.id || msg.original) return msg // Skip user's own messages or already translated
          
          try {
            const translatedContent = await translate(msg.content, userLang)
            return {
              ...msg,
              original: msg.content,
              content: translatedContent,
            }
          } catch (error) {
            console.error("🌐 Translation failed:", error)
            return msg
          }
        })
      )
      
      setMessages(translatedMessages)
    } 
    // If turning translation OFF, restore original messages
    else if (!newTranslationState && messages.length > 0) {
      console.log("🔄 Restoring original messages")
      const originalMessages = messages.map(msg => ({
        ...msg,
        content: msg.original || msg.content,
        original: undefined
      }))
      setMessages(originalMessages)
    }
  }

  // Setup socket connection
  useEffect(() => {
    if (user && token) {
      const socket = io("https://new-thriftshop.onrender.com", {
        auth: {
          token: token,
        },
        transports: ["websocket"],
      })

      socketRef.current = socket

      socket.on("connect", () => {
        console.log("✅ Connected to socket.io")

        // Only refresh messages if we don't have any messages yet
        // This prevents overwriting translated messages when socket reconnects
        if (activeConversation && messages.length === 0) {
          setTimeout(() => {
            // Small delay to ensure server has processed the connection
            refreshMessages()
          }, 1000)
        }
      })

      // FIXED: Remove the restrictive condition that only updates for active conversation
      socket.on("messages_read", (data: { message_ids: number[]; conversation_id: number }) => {
        console.log("📬 Read confirmation received:", data)

        // Update messages in the current view if it matches the conversation
        if (activeConversation && data.conversation_id === activeConversation.id) {
          setMessages((prev) =>
            prev.map((msg) => (data.message_ids.includes(msg.id) ? { ...msg, is_read: true } : msg)),
          )
        }

        // IMPORTANT: Also update the conversations list to reflect read status changes
        // This ensures the last message read status is updated in the sidebar
        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === data.conversation_id && conv.last_message) {
              // Update last message read status if it's in the read messages
              if (data.message_ids.includes(conv.last_message.id)) {
                return {
                  ...conv,
                  last_message: {
                    ...conv.last_message,
                    is_read: true,
                  },
                }
              }
            }
            return conv
          }),
        )
      })

      socket.on("new_message", (message: Message) => {
        // Show the original message immediately
        if (message.conversation_id === activeConversation?.id) {
          setMessages(prev => [...prev, { ...message }])
        } else {
          // Update unread counts
          setUnreadCounts(prev => ({
            ...prev,
            [message.conversation_id]: (prev[message.conversation_id] || 0) + 1,
          }))
        }
      
        // Update sidebar last_message immediately
        setConversations(prevConvs =>
          prevConvs.map(conv =>
            conv.id === message.conversation_id
              ? { ...conv, last_message: { ...message } }
              : conv
          )
        )
      
        // Translate the message in background if it's from another user
        ;(async () => {
          if (message.sender_id !== user?.id && isTranslationEnabled) {
            try {
              // Get the target language for this conversation (user's language)
              const targetLang = userLanguages[message.conversation_id] || "en"
              console.log(`🔄 Translating incoming message to user's language: ${targetLang}`)
              
              const translatedText = await translate(message.content, targetLang)
              console.log("🔠 Translated text:", translatedText)

              // Patch messages with translated text
              setMessages(prev =>
                prev.map(m =>
                  m.id === message.id
                    ? { ...m, original: m.content, content: translatedText }
                    : m
                )
              )
      
              // Patch last_message in sidebar
              setConversations(prev =>
                prev.map(conv =>
                  conv.id === message.conversation_id && conv.last_message?.id === message.id
                    ? {
                        ...conv,
                        last_message: {
                          ...conv.last_message,
                          original: conv.last_message.content,
                          content: translatedText,
                        },
                      }
                    : conv
                )
              )
            } catch (err) {
              console.error("🌐 Translation failed, keeping original content:", err)
            }
          }
        })()
      })

      socket.on("typing", (data: { user_id: number; conversation_id: number }) => {
        if (data.conversation_id === activeConversation?.id) {
          setOtherUserTyping(true)
          setTimeout(() => setOtherUserTyping(false), 2000) // hide after 2 seconds
        }
      })

      return () => {
        socket.disconnect()
      }
    }
  }, [user, token, activeConversation, userLanguages, isTranslationEnabled])

  // Fetch conversations
  useEffect(() => {
    if (user) {
      fetchConversations()
    }
  }, [user])

  const parseUTCTimestamp = (timestamp: string): Date => {
    // If timestamp doesn't have timezone info, treat it as UTC
    if (!timestamp.includes("Z") && !timestamp.includes("+") && !timestamp.includes("-")) {
      return new Date(timestamp + "Z")
    }
    return new Date(timestamp)
  }

  const formatConversationTime = (timestamp: string) => {
    const date = parseUTCTimestamp(timestamp)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    } else if (diffInHours < 48) {
      return "Yesterday"
    } else {
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      })
    }
  }

  const formatMessageTime = (timestamp: string) => {
    const date = parseUTCTimestamp(timestamp)
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  }

  // Detect user's language from their messages in the conversation
  const detectUserLanguageFromMessages = async (messages: Message[], conversationId: number) => {
    if (!user) return

    // Find the user's most recent messages to detect language
    const userMessages = messages
      .filter(msg => msg.sender_id === user.id)
      .slice(-3) // Take last 3 messages to get a good sample
    
    if (userMessages.length === 0) return

    // Try to detect language from the most recent message first
    for (const msg of userMessages.reverse()) {
      if (msg.content.trim().length > 10) { // Only detect from messages with sufficient content
        try {
          const detectedLang = await detectLanguage(msg.content)
          console.log(`🔍 Detected user language for conversation ${conversationId}: ${detectedLang}`)
          
          setUserLanguages(prev => ({
            ...prev,
            [conversationId]: detectedLang
          }))
          return detectedLang
        } catch (error) {
          console.error("🔍 Language detection failed:", error)
        }
      }
    }
  }

  // FIXED: Simplified message fetching without race conditions
  useEffect(() => {
    const fetchMessages = async () => {
      if (!activeConversation) return
      try {
        setLoading(true)
        const response = await api.get(`/chat/conversations/${activeConversation.id}/messages`)
        const msgs = response.data.messages.reverse()

        // First, detect the user's language from their messages
        await detectUserLanguageFromMessages(msgs, activeConversation.id)
        
        // Get the detected language or fallback to English
        const userLang = userLanguages[activeConversation.id] || "en"
        console.log(`🔄 Using language ${userLang} for conversation ${activeConversation.id}`)

        // Only translate if translation is enabled
        if (isTranslationEnabled) {
          // Translate messages from other users to the user's language
          const translatedMsgs = await Promise.all(
            msgs.map(async (msg: Message) => {
              if (msg.sender_id === user?.id) return msg
              try {
                const translatedContent = await translate(msg.content, userLang)
                return {
                  ...msg,
                  original: msg.content,
                  content: translatedContent,
                }
              } catch (error) {
                console.error("🌐 Translation failed:", error)
                return msg
              }
            })
          )
          setMessages(translatedMsgs)
        } else {
          setMessages(msgs)
        }

        // Emit read indicator immediately
        emitReadMessages(msgs, activeConversation.id)
        setUnreadCounts((prev) => {
          const updated = { ...prev }
          delete updated[activeConversation.id]
          return updated
        })
      } catch (err) {
        setError("Failed to load messages")
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    fetchMessages()
  }, [activeConversation, user]) // Removed isTranslationEnabled from dependencies

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  useEffect(() => {
    if (!activeConversation || !messages.length || !user) return

    // Emit read event for the latest unread messages sent to current user
    const unreadMessageIds = messages.filter((msg) => msg.receiver_id === user.id && !msg.is_read).map((msg) => msg.id)

    if (unreadMessageIds.length > 0 && socketRef.current) {
      socketRef.current.emit("message_read", {
        message_ids: unreadMessageIds,
        conversation_id: activeConversation.id,
      })
    }
  }, [messages, activeConversation, user])

  const emitReadMessages = (msgs: Message[], convId: number) => {
    if (!user || !socketRef.current) return

    const unreadMessageIds = msgs.filter((msg) => msg.receiver_id === user.id && !msg.is_read).map((msg) => msg.id)

    if (unreadMessageIds.length > 0) {
      socketRef.current.emit("message_read", {
        message_ids: unreadMessageIds,
        conversation_id: convId,
      })
    }
  }

  const handleConversationClick = (conversation: Conversation) => {
    setActiveConversation(null)
    setTimeout(() => {
      setActiveConversation(conversation)
    }, 50)
  }

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!newMessage.trim() || !activeConversation) return

    try {
      const response = await api.post(`/chat/conversations/${activeConversation.id}/messages`, {
        content: newMessage,
      })

      const sentMessage = response.data.message_data
      setMessages((prev) => [...prev, sentMessage])
      setNewMessage("")

      // Update last message in conversation
      setConversations((prevConvs) =>
        prevConvs.map((conv) => (conv.id === activeConversation.id ? { ...conv, last_message: sentMessage } : conv)),
      )

      // Detect language from the sent message and update user's language for this conversation
      if (sentMessage.content.trim().length > 10) {
        try {
          const detectedLang = await detectLanguage(sentMessage.content)
          console.log(`🔍 Updated user language for conversation ${activeConversation.id}: ${detectedLang}`)
          
          setUserLanguages(prev => ({
            ...prev,
            [activeConversation.id]: detectedLang
          }))
        } catch (error) {
          console.error("🔍 Language detection failed for sent message:", error)
        }
      }
    } catch (err) {
      setError("Failed to send message")
      console.error(err)
    }
  }

  const getOtherUser = (conversation: Conversation) => {
    if (!user) return null
    return conversation.user1.id === user.id ? conversation.user2 : conversation.user1
  }

  const getProfilePictureUrl = (path?: string) => {
    if (!path) return "/placeholder.svg";
  
    // If the string already starts with http(s), it's a full URL from Cloudinary.
    return /^https?:\/\//i.test(path)
      ? path
      : `https://new-thriftshop.onrender.com/static/uploads/${path}`;
  };

  const refreshMessages = async () => {
    if (!activeConversation) return

    try {
      const response = await api.get(`/chat/conversations/${activeConversation.id}/messages`)
      const freshMessages = response.data.messages.reverse()
      
      // If translation is enabled, apply translations to the fresh messages
      if (isTranslationEnabled) {
        const userLang = userLanguages[activeConversation.id] || "en"
        const translatedMessages = await Promise.all(
          freshMessages.map(async (msg: Message) => {
            if (msg.sender_id === user?.id) return msg
            try {
              const translatedContent = await translate(msg.content, userLang)
              return {
                ...msg,
                original: msg.content,
                content: translatedContent,
              }
            } catch (error) {
              console.error("🌐 Translation failed:", error)
              return msg
            }
          })
        )
        setMessages(translatedMessages)
      } else {
        setMessages(freshMessages)
      }
    } catch (err) {
      console.error("Failed to refresh messages", err)
    }
  }

  if (loading && conversations.length === 0) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </>
    )
  }

  return (
    <>
      <Navbar />
      <div className="chat-container">
        <div className="container">
          <div className="chat-content">
            <div className="chat-sidebar">
              <h2 className="chat-title">CHATS</h2>
              <div className="chat-list">
                {loading ? (
                  <div className="loading">Loading conversations...</div>
                ) : conversations.length === 0 ? (
                  <div className="no-conversations">No conversations</div>
                ) : (
                  conversations.map((conv) => {
                    const other = getOtherUser(conv)
                    return (
                      <div
                        key={conv.id}
                        className={`chat-item ${activeConversation?.id === conv.id ? "active" : ""} ${
                          unreadCounts[conv.id] ? "unread-glow" : ""
                        }`}
                        onClick={() => handleConversationClick(conv)}
                      >
                        <div className="chat-avatar">
                          {other?.profile_picture ? (
                            <img
                              src={getProfilePictureUrl(other?.profile_picture) || "/placeholder.svg"}
                              alt={other?.username}
                            />
                          ) : (
                            <div className="avatar-placeholder">{other?.username[0]}</div>
                          )}
                        </div>
                        <div className="chat-info">
                          <div className="chat-header">
                            <h3 className="chat-username">{other?.username}</h3>
                            <div className="chat-time-badge-wrapper">
                              <span className="chat-time">
                                {conv.last_message ? adjustUtcString(conv.last_message.created_at) : ""}
                              </span>
                              {unreadCounts[conv.id] > 0 && <span className="unread-badge">new</span>}
                            </div>
                          </div>

                          <p className="chat-last-message">
                            {conv.last_message?.content ? conv.last_message.content.slice(0, 30) : "No messages yet"}
                          </p>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            <div className="chat-main">
              {activeConversation ? (
                <>
                  <div className="chat-header-bar">
                    <button
                      onClick={handleTranslationToggle}
                      className="translation-toggle"
                    >
                      🌍 Auto Translate: {isTranslationEnabled ? "ON" : "OFF"}
                    </button>

                    <div className="chat-user-info">
                      <div className="chat-user-avatar">
                        {getOtherUser(activeConversation)?.profile_picture ? (
                          <img
                            src={
                              getProfilePictureUrl(getOtherUser(activeConversation)?.profile_picture) ||
                              "/placeholder.svg" ||
                              "/placeholder.svg"
                            }
                            alt={getOtherUser(activeConversation)?.username}
                          />
                        ) : (
                          <div className="avatar-placeholder">{getOtherUser(activeConversation)?.username[0]}</div>
                        )}
                      </div>
                      <h3 className="chat-user-name">{getOtherUser(activeConversation)?.username}</h3>
                      {/* Show detected language indicator */}
                      {userLanguages[activeConversation.id] && (
                        <span className="language-indicator">
                          🌐 {userLanguages[activeConversation.id].toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="messages-container">
                    <div className="messages-list">
                      {messages.map((msg) => (
                        <div key={msg.id} className={`message ${msg.sender_id === user?.id ? "sent" : "received"}`}>
                          <div className="message-content">
                            {msg.original && msg.sender_id !== user?.id && (
                              <p className="original-text">{msg.original}</p>
                            )}
                            <p className="translated-text">{msg.content}</p>
                            <span className="message-time-with-status">
                              {adjustUtcString(msg.created_at)}
                              {msg.sender_id === user?.id && (
                                <span className="read-indicator">{msg.is_read ? "✓✓" : "✓"}</span>
                              )}
                            </span>
                          </div>
                        </div>
                      ))}
                      {otherUserTyping && (
                        <div className="typing-indicator">
                          <em>{getOtherUser(activeConversation)?.username} is typing...</em>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>

                  <form onSubmit={handleSendMessage} className="message-form">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value)
                        if (socketRef.current && activeConversation) {
                          socketRef.current.emit("typing", {
                            conversation_id: activeConversation.id,
                            token: token,
                          })
                        }
                      }}
                      placeholder="Type your message"
                      className="message-input"
                    />
                    <button type="submit" className="send-button" disabled={!newMessage.trim()}>
                      Send
                    </button>
                  </form>
                </>
              ) : (
                <div className="no-chat-selected">
                  <p>Select a chat to start messaging</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
    </>
  )
}

export default Chat